import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { AppWindow, AlertTriangle, Search, Filter, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { getAppThreats } from '../services/api';
import { toast } from 'sonner';

const threatClassColors = {
  critical: 'bg-red-500/10 text-red-500 border-red-500/20',
  high: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
  medium: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
  low: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  safe: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
};

const categoryColors = {
  malware: 'bg-red-500/10 text-red-500',
  shadow_it: 'bg-amber-500/10 text-amber-500',
  suspicious_behavior: 'bg-orange-500/10 text-orange-500',
  normal: 'bg-emerald-500/10 text-emerald-500',
};

export default function AppThreats() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterClass, setFilterClass] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getAppThreats();
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch app threats:', error);
      toast.error('Failed to load app threats');
    } finally {
      setLoading(false);
    }
  };

  const filteredEvents = events.filter((event) => {
    const matchesSearch = event.process_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterClass === 'all' || event.classification === filterClass;
    return matchesSearch && matchesFilter;
  });

  const stats = {
    total: events.length,
    critical: events.filter((e) => e.classification === 'critical').length,
    high: events.filter((e) => e.classification === 'high').length,
    shadowIt: events.filter((e) => e.category === 'shadow_it').length,
  };

  return (
    <div className="space-y-6" data-testid="app-threats-page">
      {/* Header */}
      <div>
        <h1 className="font-heading font-bold text-3xl text-white">App Threats</h1>
        <p className="text-slate-400 mt-1">Monitor application and process behavior anomalies</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <AppWindow className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold text-white">{stats.total}</p>
                <p className="text-xs text-slate-500">Total Events</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold text-white">{stats.critical}</p>
                <p className="text-xs text-slate-500">Critical</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold text-white">{stats.high}</p>
                <p className="text-xs text-slate-500">High Risk</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <AppWindow className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold text-white">{stats.shadowIt}</p>
                <p className="text-xs text-slate-500">Shadow IT</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader className="border-b border-slate-800">
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <CardTitle className="text-lg font-heading text-white">Application Events</CardTitle>
            <div className="flex gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  placeholder="Search processes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 w-48 bg-slate-800 border-slate-700 text-white"
                />
              </div>
              <Select value={filterClass} onValueChange={setFilterClass}>
                <SelectTrigger className="w-36 bg-slate-800 border-slate-700 text-white">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="safe">Safe</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="icon" onClick={fetchData} className="border-slate-700">
                <RefreshCw className="w-4 h-4 text-slate-400" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableHead className="text-slate-400">Process Name</TableHead>
                <TableHead className="text-slate-400">Category</TableHead>
                <TableHead className="text-slate-400">Risk Score</TableHead>
                <TableHead className="text-slate-400">Classification</TableHead>
                <TableHead className="text-slate-400">Timestamp</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEvents.map((event, index) => (
                <motion.tr
                  key={event.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.02 }}
                  className="border-slate-800 hover:bg-slate-800/50"
                >
                  <TableCell className="font-mono text-sm text-white">{event.process_name}</TableCell>
                  <TableCell>
                    <Badge className={categoryColors[event.category] || 'bg-slate-800 text-slate-400'}>
                      {event.category?.replace(/_/g, ' ')}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-12 h-2 bg-slate-800 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            event.risk_score >= 75 ? 'bg-red-500' :
                            event.risk_score >= 50 ? 'bg-orange-500' :
                            event.risk_score >= 25 ? 'bg-amber-500' : 'bg-emerald-500'
                          }`}
                          style={{ width: `${event.risk_score}%` }}
                        />
                      </div>
                      <span className="text-sm text-slate-400">{event.risk_score}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={`${threatClassColors[event.classification]} border`}>
                      {event.classification}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-slate-400">
                    {new Date(event.timestamp).toLocaleString()}
                  </TableCell>
                </motion.tr>
              ))}
              {filteredEvents.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                    No application events found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
